import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import {
  searchMedications, getMedicationDetails,
  MEDICATION_FREQUENCIES, MEDICATION_UNITS, MEDICATION_FORMS,
} from '../../services/medicationService';
import { timesForFrequency, unitFromStrength } from '../../services/medicationNormalize';
import {
  addMedication, getMedications, logMedicationTaken, deleteMedication,
  addSupplementLog, getSupplementLogs, deleteSupplementLog,
} from '../../services/firestoreService';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

function emptyMedForm() {
  return {
    name: '',
    brandName: '',
    dosage: '',
    unit: 'mg',
    dosageForm: 'Tablet',
    frequency: 'Once daily',
    times: ['08:00'],
    startDate: format(new Date(), 'yyyy-MM-dd'),
    reason: '',
    notes: '',
    remindersEnabled: false,
  };
}

// ─── Supplement search via Open Food Facts ─────────────────
async function searchSupplements(query) {
  const base = 'https://world.openfoodfacts.org/cgi/search.pl';
  const common = `search_terms=${encodeURIComponent(query)}&search_simple=1&action=process&json=1&page_size=20`;

  const parse = (products) =>
    (products || [])
      .filter(p => p.product_name)
      .map(p => {
        const n = p.nutriments || {};
        const cal  = n['energy-kcal_serving'] || n['energy-kcal'] || 0;
        const prot = n['proteins_serving']     || n['proteins']    || 0;
        const fat  = n['fat_serving']          || n['fat']         || 0;
        const carb = n['carbohydrates_serving']|| n['carbohydrates']|| 0;
        const nutrients = {};
        if (cal  > 0) nutrients.calories = Math.round(cal);
        if (prot > 0) nutrients.protein  = +prot.toFixed(1);
        if (fat  > 0) nutrients.fat      = +fat.toFixed(1);
        if (carb > 0) nutrients.carbs    = +carb.toFixed(1);
        return {
          id: p.code || p._id || p.product_name,
          name: p.product_name,
          brand: p.brands || '',
          servingSize: p.serving_size || '',
          nutrients,
          image: p.image_thumb_url || p.image_url || '',
        };
      });

  try {
    const res1 = await fetch(`${base}?${common}&categories_tags=supplements`);
    const json1 = await res1.json();
    let results = parse(json1.products);

    if (results.length < 3) {
      const res2 = await fetch(`${base}?${common}`);
      const json2 = await res2.json();
      const seen = new Set(results.map(r => r.id));
      results = [...results, ...parse(json2.products).filter(r => !seen.has(r.id))];
    }

    return results.slice(0, 20);
  } catch {
    return [];
  }
}

export default function MedicationTracker() {
  const { theme } = useTheme();
  const { user } = useAuth();

  // ── Medication state ──
  const [medications, setMedications] = useState([]);
  const [medSearchQuery, setMedSearchQuery] = useState('');
  const [medSearchResults, setMedSearchResults] = useState([]);
  const [medSearching, setMedSearching] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [choiceDetails, setChoiceDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [customStrength, setCustomStrength] = useState(false);
  const [strengthLabel, setStrengthLabel] = useState('');
  const [medForm, setMedForm] = useState(emptyMedForm());

  // ── Supplement state ──
  const [suppResults, setSuppResults] = useState([]);
  const [suppQuery, setSuppQuery] = useState('');
  const [suppSearching, setSuppSearching] = useState(false);
  const [suppLogs, setSuppLogs] = useState([]);
  const [suppDosage, setSuppDosage] = useState({});
  const [suppNotes, setSuppNotes] = useState({});

  const [tab, setTab] = useState('list');
  const debounceRef = useRef(null);
  const searchAbortRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadMeds();
      loadSuppLogs();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const loadMeds = async () => {
    const data = await getMedications(user.uid);
    setMedications(data);
  };

  const loadSuppLogs = async () => {
    const data = await getSupplementLogs(user.uid);
    setSuppLogs(data);
  };

  const resetAddForm = () => {
    setMedForm(emptyMedForm());
    setSelectedChoice(null);
    setChoiceDetails(null);
    setManualMode(false);
    setCustomStrength(false);
    setStrengthLabel('');
    setMedSearchQuery('');
    setMedSearchResults([]);
  };

  const handleMedSearchChange = (val) => {
    setMedSearchQuery(val);
    if (selectedChoice) {
      setSelectedChoice(null);
      setChoiceDetails(null);
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchAbortRef.current) searchAbortRef.current = true;
    if (val.trim().length < 2) {
      setMedSearchResults([]);
      setMedSearching(false);
      return;
    }
    setMedSearching(true);
    const token = {};
    searchAbortRef.current = token;
    debounceRef.current = setTimeout(async () => {
      const results = await searchMedications(val);
      if (searchAbortRef.current !== token) return;
      setMedSearchResults(results);
      setMedSearching(false);
    }, 300);
  };

  const selectMedFromSearch = async (choice) => {
    setSelectedChoice(choice);
    setMedSearchResults([]);
    setMedSearchQuery('');
    setManualMode(false);
    setCustomStrength(false);
    setMedForm(prev => ({
      ...prev,
      name: choice.name,
      brandName: choice.commonBrand || '',
      dosageForm: choice.formsPreview?.[0] || prev.dosageForm || 'Tablet',
    }));
    setDetailsLoading(true);
    try {
      const details = await getMedicationDetails(choice);
      setChoiceDetails(details);
      const firstStrength = details.strengths[0];
      const firstForm = details.forms[0];
      if (firstStrength) setStrengthLabel(firstStrength.label);
      setMedForm(prev => ({
        ...prev,
        brandName: prev.brandName || details.brands[0] || '',
        dosage: firstStrength
          ? (firstStrength.label.includes('/') ? firstStrength.label : String(firstStrength.amount))
          : prev.dosage,
        unit: firstStrength ? unitFromStrength(firstStrength) : prev.unit,
        dosageForm: firstForm || prev.dosageForm,
      }));
      if (!details.strengths.length) setCustomStrength(true);
    } catch {
      setChoiceDetails(null);
      setCustomStrength(true);
    } finally {
      setDetailsLoading(false);
    }
  };

  const applyStrength = (label) => {
    if (label === '__custom__') {
      setCustomStrength(true);
      setStrengthLabel('');
      return;
    }
    setCustomStrength(false);
    setStrengthLabel(label);
    const match = (choiceDetails?.strengths || []).find(s => s.label === label);
    if (!match) return;
    setMedForm(prev => ({
      ...prev,
      dosage: match.label.includes('/') ? match.label : String(match.amount),
      unit: unitFromStrength(match),
    }));
  };

  const handleFrequencyChange = (frequency) => {
    setMedForm(prev => ({
      ...prev,
      frequency,
      times: timesForFrequency(frequency, prev.times),
    }));
  };

  const handleTimeChange = (idx, value) => {
    setMedForm(prev => {
      const times = [...(prev.times || [])];
      times[idx] = value;
      return { ...prev, times };
    });
  };

  const addTimeSlot = () => {
    setMedForm(prev => ({ ...prev, times: [...(prev.times || []), '08:00'] }));
  };

  const removeTimeSlot = (idx) => {
    setMedForm(prev => ({ ...prev, times: (prev.times || []).filter((_, i) => i !== idx) }));
  };

  const handleAddMed = async (e) => {
    e.preventDefault();
    if (!medForm.name) return;
    try {
      await addMedication(user.uid, {
        ...medForm,
        source: selectedChoice ? 'openfda-ndc' : 'manual',
        genericKey: selectedChoice?.id || '',
      });
      toast.success('Medication added!');
      resetAddForm();
      setTab('list');
      loadMeds();
    } catch {
      toast.error('Failed to add medication');
    }
  };

  const handleTaken = async (med) => {
    try {
      await logMedicationTaken(user.uid, { medicationId: med.id, medicationName: med.name, dosage: `${med.dosage} ${med.unit}` });
      toast.success(`${med.name} logged as taken`);
    } catch {
      toast.error('Failed to log');
    }
  };

  const handleDelete = async (medId) => {
    try {
      await deleteMedication(user.uid, medId);
      toast.success('Medication removed');
      loadMeds();
    } catch {
      toast.error('Failed to remove');
    }
  };

  // ── Supplement handlers ──
  const handleSuppSearch = async () => {
    if (!suppQuery.trim()) return;
    setSuppSearching(true);
    const results = await searchSupplements(suppQuery);
    setSuppResults(results);
    setSuppSearching(false);
    if (results.length === 0) toast('No results. Try a different name.', { icon: '🔍' });
  };

  const handleLogSupp = async (supp) => {
    try {
      const dosage = suppDosage[supp.id] || '';
      const notes = suppNotes[supp.id] || '';
      await addSupplementLog(user.uid, {
        name: supp.name,
        brand: supp.brand,
        dosage,
        notes,
        servingSize: supp.servingSize || '',
        nutrients: supp.nutrients,
      });
      toast.success(`${supp.name.slice(0, 30)} logged`);
      loadSuppLogs();
    } catch {
      toast.error('Failed to log supplement');
    }
  };

  const handleDeleteSuppLog = async (logId) => {
    try {
      await deleteSupplementLog(user.uid, logId);
      loadSuppLogs();
      toast.success('Log removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const tabStyle = (t) => ({
    padding: '0.6rem 1.25rem', border: 'none', cursor: 'pointer',
    background: tab === t ? theme.accent : 'transparent',
    color: tab === t ? '#fff' : theme.textSecondary,
    borderRadius: '8px', fontFamily: "'Exo 2', sans-serif",
    fontWeight: tab === t ? 600 : 400, transition: 'all 0.2s ease',
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }} className="animate-fade">
      <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: theme.text, marginBottom: '2rem' }}>
        💊 Medication &amp; Supplement Tracker
      </h1>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button style={tabStyle('list')} onClick={() => setTab('list')}>My Medications</button>
        <button style={tabStyle('add')} onClick={() => setTab('add')}>+ Add Medication</button>
        <button style={tabStyle('supplements')} onClick={() => setTab('supplements')}>🌿 Supplements</button>
      </div>

      {/* ── Medications List ── */}
      {tab === 'list' && (
        <div>
          {medications.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💊</div>
              <p>No medications added yet.</p>
              <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => setTab('add')}>Add First Medication</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {medications.map(med => (
                <div key={med.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: theme.text, fontSize: '1.05rem', marginBottom: '0.15rem' }}>{med.name}</div>
                      {med.brandName && (
                        <div style={{ color: theme.accent, fontSize: '0.8rem', marginBottom: '0.2rem' }}>
                          Common brand: {med.brandName}
                        </div>
                      )}
                      <div style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>
                        {[
                          med.dosage && `${med.dosage} ${med.unit || ''}`.trim(),
                          med.dosageForm,
                          med.frequency,
                        ].filter(Boolean).join(' · ')}
                      </div>
                      {med.times?.length > 0 && (
                        <div style={{ color: theme.textSecondary, fontSize: '0.78rem', marginTop: '0.2rem' }}>
                          {med.remindersEnabled ? '⏰ ' : ''}
                          {med.times.join(' · ')}
                        </div>
                      )}
                      {med.reason && (
                        <div style={{ color: theme.textSecondary, fontSize: '0.8rem', marginTop: '0.2rem' }}>{med.reason}</div>
                      )}
                      {med.notes && <div style={{ color: theme.textSecondary, fontSize: '0.8rem', marginTop: '0.25rem', fontStyle: 'italic' }}>{med.notes}</div>}
                      <div style={{ color: theme.textSecondary, fontSize: '0.75rem', marginTop: '0.5rem' }}>Started: {med.startDate}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button className="btn btn-primary" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }} onClick={() => handleTaken(med)}>
                        ✅ Taken
                      </button>
                      <button className="btn btn-ghost" style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: theme.danger, borderColor: theme.danger }}
                        onClick={() => handleDelete(med.id)}>🗑</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Add Medication ── */}
      {tab === 'add' && (
        <div className="card">
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, marginBottom: '0.4rem', color: theme.text }}>Add Medication</h3>
          <p style={{ color: theme.textSecondary, fontSize: '0.85rem', marginBottom: '1.25rem' }}>
            Search by generic or brand name. Results are grouped so you pick the medication, not a manufacturer.
          </p>

          <div className="form-group">
            <label>Search medications</label>
            <div style={{ position: 'relative' }}>
              {medSearching && (
                <>
                  <style>{`@keyframes med-spin { to { transform: rotate(360deg) } }`}</style>
                  <div style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    width: '14px', height: '14px',
                    border: `2px solid ${theme.border}`,
                    borderTop: `2px solid ${theme.accent}`,
                    borderRadius: '50%',
                    animation: 'med-spin 0.7s linear infinite',
                    pointerEvents: 'none', zIndex: 1,
                  }} />
                </>
              )}
              <input
                value={medSearchQuery}
                onChange={e => handleMedSearchChange(e.target.value)}
                onBlur={() => setTimeout(() => setMedSearchResults([]), 180)}
                placeholder="Try atorva, lipitor, metformin, lisinopril..."
                style={{ paddingRight: medSearching ? '2.5rem' : undefined, width: '100%' }}
                autoComplete="off"
              />
            </div>
          </div>

          {medSearchResults.length > 0 && (
            <div style={{ background: theme.bgSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', marginBottom: '1rem', overflow: 'hidden' }}>
              {medSearchResults.map((med) => (
                <div key={med.id}
                  style={{ padding: '0.85rem 1rem', cursor: 'pointer', borderBottom: `1px solid ${theme.border}`, transition: 'background 0.2s' }}
                  onMouseDown={e => e.preventDefault()}
                  onMouseEnter={e => e.currentTarget.style.background = theme.bgCard}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => selectMedFromSearch(med)}>
                  <div style={{ fontWeight: 600, color: theme.text }}>{med.name}</div>
                  {med.commonBrand && (
                    <div style={{ color: theme.textSecondary, fontSize: '0.8rem', marginTop: '0.15rem' }}>
                      Common brand: {med.commonBrand}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!selectedChoice && !manualMode && medSearchQuery.trim().length >= 2 && !medSearching && medSearchResults.length === 0 && (
            <p style={{ color: theme.textSecondary, fontSize: '0.8rem', margin: '-0.5rem 0 1rem' }}>
              No matches. Try a different spelling, or enter the medication manually.
            </p>
          )}

          {!selectedChoice && !manualMode && (
            <button type="button" className="btn btn-ghost" style={{ marginBottom: '0.5rem' }} onClick={() => setManualMode(true)}>
              Can't find it? Enter manually
            </button>
          )}

          {selectedChoice && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem',
              background: theme.bgSecondary, border: `1px solid ${theme.border}`, borderRadius: '10px',
              padding: '0.85rem 1rem', marginBottom: '1.25rem',
            }}>
              <div>
                <div style={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textSecondary, marginBottom: '0.2rem' }}>Selected</div>
                <div style={{ fontWeight: 700, color: theme.text, fontFamily: "'Rajdhani', sans-serif", fontSize: '1.15rem' }}>{selectedChoice.name}</div>
                {medForm.brandName && (
                  <div style={{ color: theme.textSecondary, fontSize: '0.82rem' }}>Common brand: {medForm.brandName}</div>
                )}
              </div>
              <button type="button" className="btn btn-ghost" style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem' }} onClick={resetAddForm}>
                Change
              </button>
            </div>
          )}

          {(selectedChoice || manualMode) && (
          <form onSubmit={handleAddMed}>
            <div className="form-group">
              <label>Medication Name *</label>
              <input value={medForm.name} onChange={e => setMedForm({ ...medForm, name: e.target.value })} placeholder="e.g. Atorvastatin" required />
            </div>

            {((choiceDetails?.brands?.length > 1) || medForm.brandName || manualMode) && (
              <div className="form-group">
                <label>Common brand name</label>
                {choiceDetails?.brands?.length > 0 ? (
                  <select value={medForm.brandName} onChange={e => setMedForm({ ...medForm, brandName: e.target.value })}>
                    <option value="">None / generic</option>
                    {choiceDetails.brands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                ) : (
                  <input value={medForm.brandName} onChange={e => setMedForm({ ...medForm, brandName: e.target.value })} placeholder="e.g. Lipitor (optional)" />
                )}
              </div>
            )}

            {detailsLoading && (
              <p style={{ color: theme.textSecondary, fontSize: '0.8rem', marginBottom: '1rem' }}>Loading strengths and forms from the drug database…</p>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
              <div className="form-group">
                <label>Dosage / strength</label>
                {!customStrength && choiceDetails?.strengths?.length > 0 ? (
                  <select
                    value={strengthLabel || choiceDetails.strengths[0].label}
                    onChange={e => applyStrength(e.target.value)}
                  >
                    {choiceDetails.strengths.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
                    <option value="__custom__">Other (type it in)</option>
                  </select>
                ) : (
                  <input type="text" value={medForm.dosage} onChange={e => setMedForm({ ...medForm, dosage: e.target.value })} placeholder="40" />
                )}
                {customStrength && choiceDetails?.strengths?.length > 0 && (
                  <button type="button" className="btn btn-ghost" style={{ marginTop: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem' }} onClick={() => { setCustomStrength(false); applyStrength(choiceDetails.strengths[0].label); }}>
                    Choose from database strengths
                  </button>
                )}
              </div>
              <div className="form-group">
                <label>Unit</label>
                <select value={medForm.unit} onChange={e => setMedForm({ ...medForm, unit: e.target.value })}>
                  {MEDICATION_UNITS.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Dosage form</label>
              <select value={medForm.dosageForm} onChange={e => setMedForm({ ...medForm, dosageForm: e.target.value })}>
                {(choiceDetails?.forms?.length ? choiceDetails.forms : MEDICATION_FORMS).map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
                {choiceDetails?.forms?.length > 0 && !MEDICATION_FORMS.includes(medForm.dosageForm) && (
                  <option value={medForm.dosageForm}>{medForm.dosageForm}</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>Frequency</label>
              <select value={medForm.frequency} onChange={e => handleFrequencyChange(e.target.value)}>
                {MEDICATION_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Time(s) taken</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {(medForm.times || []).map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type="time" value={t} onChange={e => handleTimeChange(i, e.target.value)} style={{ maxWidth: 180 }} />
                    {(medForm.times || []).length > 1 && (
                      <button type="button" className="btn btn-ghost" style={{ padding: '0.35rem 0.6rem' }} onClick={() => removeTimeSlot(i)}>Remove</button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-ghost" style={{ alignSelf: 'flex-start', padding: '0.35rem 0.7rem', fontSize: '0.8rem' }} onClick={addTimeSlot}>
                  + Add time
                </button>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', textTransform: 'none', letterSpacing: 0, fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!medForm.remindersEnabled}
                  onChange={e => setMedForm({ ...medForm, remindersEnabled: e.target.checked })}
                  style={{ width: 16, height: 16 }}
                />
                Remind me at these times
              </label>
            </div>

            <div className="form-group">
              <label>Start date</label>
              <input type="date" value={medForm.startDate} onChange={e => setMedForm({ ...medForm, startDate: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Reason / purpose (optional)</label>
              <input value={medForm.reason} onChange={e => setMedForm({ ...medForm, reason: e.target.value })} placeholder="e.g. Cholesterol, blood pressure..." />
            </div>

            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea value={medForm.notes} onChange={e => setMedForm({ ...medForm, notes: e.target.value })}
                placeholder="e.g. Take with food, avoid grapefruit..." rows={2} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" className="btn btn-ghost" onClick={() => { resetAddForm(); setTab('list'); }}>Cancel</button>
              <button type="submit" className="btn btn-primary">+ Add Medication</button>
            </div>
          </form>
          )}
        </div>
      )}

      {/* ── Supplement Tracker ── */}
      {tab === 'supplements' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, marginBottom: '0.5rem', color: theme.text }}>🌿 Search Supplements</h3>
            <p style={{ color: theme.textSecondary, fontSize: '0.85rem', marginBottom: '1rem' }}>
              Search the Open Food Facts database for supplements and vitamins.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                value={suppQuery}
                onChange={e => setSuppQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSuppSearch()}
                placeholder="Search supplements — Nature Made, fish oil, vitamin B12, creatine, magnesium..."
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary" onClick={handleSuppSearch} disabled={suppSearching}>
                {suppSearching ? '...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {suppResults.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: theme.textSecondary, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
                Results ({suppResults.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {suppResults.map(supp => (
                  <div key={supp.id} className="card" style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.2rem' }}>
                          {supp.image && <img src={supp.image} alt="" style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }} />}
                          <div style={{ fontWeight: 600, color: theme.text, fontSize: '0.95rem' }}>
                            {supp.name.length > 70 ? supp.name.slice(0, 70) + '…' : supp.name}
                          </div>
                        </div>
                        {supp.brand && <div style={{ color: theme.textSecondary, fontSize: '0.8rem', marginBottom: '0.4rem' }}>{supp.brand}</div>}
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          {supp.servingSize && (
                            <span style={{ color: theme.textSecondary, fontSize: '0.78rem' }}>
                              Serving: {supp.servingSize}
                            </span>
                          )}
                          {supp.nutrients.calories && (
                            <span style={{ color: theme.accent, fontSize: '0.78rem', fontWeight: 600 }}>
                              {supp.nutrients.calories} cal
                            </span>
                          )}
                          {supp.nutrients.protein && (
                            <span style={{ color: theme.textSecondary, fontSize: '0.78rem' }}>P: {supp.nutrients.protein}g</span>
                          )}
                          {supp.nutrients.fat && (
                            <span style={{ color: theme.textSecondary, fontSize: '0.78rem' }}>F: {supp.nutrients.fat}g</span>
                          )}
                          {supp.nutrients.carbs && (
                            <span style={{ color: theme.textSecondary, fontSize: '0.78rem' }}>C: {supp.nutrients.carbs}g</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                          <input
                            value={suppDosage[supp.id] || ''}
                            onChange={e => setSuppDosage(prev => ({ ...prev, [supp.id]: e.target.value }))}
                            placeholder="Dosage (e.g. 2 capsules, 1000mg)"
                            style={{ flex: 1, minWidth: '160px', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
                          />
                          <input
                            value={suppNotes[supp.id] || ''}
                            onChange={e => setSuppNotes(prev => ({ ...prev, [supp.id]: e.target.value }))}
                            placeholder="Notes (optional)"
                            style={{ flex: 1, minWidth: '120px', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
                          />
                        </div>
                      </div>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', flexShrink: 0, alignSelf: 'flex-start' }}
                        onClick={() => handleLogSupp(supp)}
                      >
                        + Log
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Today's Supplement Log */}
          <div>
            <h4 style={{ color: theme.textSecondary, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
              Recent Supplement Log ({suppLogs.length})
            </h4>
            {suppLogs.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '2rem', color: theme.textSecondary }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🌿</div>
                <p>No supplements logged yet. Search above to get started.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {suppLogs.map(log => (
                  <div key={log.id} className="card" style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: theme.text, fontSize: '0.9rem' }}>
                          {log.name?.length > 60 ? log.name.slice(0, 60) + '…' : log.name}
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
                          {log.brand && <span style={{ color: theme.textSecondary, fontSize: '0.78rem' }}>{log.brand}</span>}
                          {log.dosage && <span style={{ color: theme.accent, fontSize: '0.78rem', fontWeight: 600 }}>{log.dosage}</span>}
                          {log.notes && <span style={{ color: theme.textSecondary, fontSize: '0.78rem', fontStyle: 'italic' }}>{log.notes}</span>}
                        </div>
                        <div style={{ color: theme.textSecondary, fontSize: '0.72rem', marginTop: '0.2rem' }}>
                          {log.loggedAt?.toDate ? format(log.loggedAt.toDate(), 'MMM d, h:mm a') : 'Just now'}
                        </div>
                      </div>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger, fontSize: '1.1rem', padding: '0' }}
                        onClick={() => handleDeleteSuppLog(log.id)}
                      >🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
